const patientContextSidebar = {
  demographicsGrid: {
    // U+2014 em-dash placeholder rendered in place of a `null` demographic value
    // (e.g. unknown / not collected). Lives here so translators / product can swap
    // the glyph for a localized fallback word ("Unknown", "N/A") without a code
    // change. The component owns this visible fallback per the FRONTEND.md i18n rule.
    emptyValueFallback: "—",
  },
  labInterpPrimaryCardFooter: {
    // Visible text for the lab-results PDF link (download icon is decorative —
    // the accessible name comes from this string).
    viewPdfLabel: "Latest lab results (PDF)",
    // Rendered as a prefix on the tested row: "Tested {{date}}".
    testedAtPrefix: "Tested {{date}}",
    // U+2014 em-dash rendered when `testedAt` or `orderingPractitioner` is null.
    // Component-owned per FRONTEND.md so translators can swap the glyph for a
    // localized fallback ("Unknown", "N/A") without a code change.
    emptyValueFallback: "—",
  },
  nthTestCycleIndicator: {
    // English ordinal categories per Intl.PluralRules — pick one based on the
    // ordinal value (1 → "one", 2 → "two", 3 → "few", 4+ → "other"; 21 → "one"
    // for "21st test", etc.). Component picks the variant; the strings stay
    // colocated for translator context. Interpolation variable is `cycle` (not
    // `ordinal`) because react-i18next reserves `ordinal` as a boolean toggle.
    one: "{{cycle}}st test cycle",
    two: "{{cycle}}nd test cycle",
    few: "{{cycle}}rd test cycle",
    other: "{{cycle}}th test cycle",
  },
  patientHeader: {
    patientIdLabel: "ID {{patientId}}",
    viewPatientDetailsLabel: "View patient details for {{name}}",
  },
  patientSurveysAccordion: {
    // Pill shown on a survey's accordion trigger when `completedAt === null`.
    // The patient never finished (or hasn't yet been served) this survey.
    notCompletedBadge: "Not completed",
    // Body copy rendered inside an expanded accordion item for a survey that
    // has not been completed (its `sections[]` are empty by contract).
    notCompletedMessage: "This survey hasn't been completed yet.",
    // Leading line inside a completed survey's expanded content, above the
    // sections. `date` is the formatted `completedAt` submission date.
    completedOn: "Completed {{date}}",
    // Empty state when the consumer passes no surveys at all. Keeps the drawer
    // legible instead of rendering a bare, empty accordion shell.
    noSurveysMessage: "No surveys to display.",
    // Section header treatment when a section carries a count, e.g. "Goals (2)".
    // `label` is data (the mapper supplies the section label); only the
    // parenthetical structure is owned here so translators can localize it.
    sectionLabelWithCount: "{{label}} ({{count}})",
  },
  patientTimelineSecondaryCard: {
    // Card heading above the step list.
    title: "Patient timeline",
    // Visible label for the single "View prior wellness plan" CTA on the
    // Wellness Plan timeline card. The accompanying prescription icon is
    // decorative — the accessible name comes from this string. Disabled when
    // hasPriorWellnessPlan is false (e.g. no prior plan to view).
    viewPriorPlanLabel: "View prior wellness plan",
    // Visually-hidden, screen-reader-only labels announcing each timeline step's
    // completion state. The marker icon is decorative (aria-hidden), so sighted
    // users read completion from the icon while AT users get it from this text.
    stepCompletedLabel: "(completed)",
    stepPendingLabel: "(pending)",
  },
  priorTestSecondaryCard: {
    // Card heading above the prior-test links + metadata.
    title: "Prior test",
    // Rendered as the prior collection date row: "Tested {{date}}". The leading
    // calendar icon is decorative — the accessible name comes from this string.
    // Mirrors labInterpPrimaryCardFooter.testedAtPrefix; kept separate so
    // translators can localize the "prior test" context independently.
    testedAtPrefix: "Tested {{date}}",
    // Visible label for the "View prior interpretation" CTA. Disabled when
    // hasPriorInterpretation is false — but in practice the entire card hides in
    // that case, so the disabled state ships only as a safety affordance.
    viewPriorInterpretationLabel: "View prior interpretation",
    // Visible label for the "View prior wellness plan" CTA. Disabled when
    // hasPriorWellnessPlan is false (e.g. prior cycle had no plan).
    viewPriorPlanLabel: "View prior wellness plan",
    // Visible label for the prior lab-results PDF CTA. Disabled when
    // hasPriorLabResultsPdf is false (e.g. prior result has no PDF on file).
    downloadPriorPdfLabel: "Previous lab results (PDF)",
    // U+2014 em-dash rendered when `priorTestedAt` or `priorPractitioner` is null.
    // Component-owned per FRONTEND.md so translators can swap the glyph for a
    // localized fallback ("Unknown", "N/A") without a code change.
    emptyValueFallback: "—",
  },
  wellnessPlanPrimaryCardFooter: {
    priceBreakdown: {
      title: "Price breakdown",
      productsSubtotal: "Products subtotal",
      followUpLabsSubtotal: "Follow-up labs subtotal",
      total: "Total",
    },
    paymentOptions: {
      title: "Payment options",
      toggleLabel: "Practitioner pays for follow-up labs",
      comingSoon: "Coming soon",
    },
    ctaSlotLabel: "Wellness plan actions",
  },
} as const;

export { patientContextSidebar };
